import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import Router from 'next/router';
import { createElement, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import {
  invalidateInterruptedSessions,
  useQueryInterruptedSessions,
} from '@/app-shell/chronicle/hooks/query/useQueryInterruptedSessions';
import { chronicleLogger as logger } from '@/app-shell/chronicle/utils/chronicle-logger';
import {
  useIsReleaseRemoteSyncAsyncWebEnabled,
  useIsRemoteNewSessionLogicEnabled,
} from '@/app-shell/flags/desktop-flags';
import { useIsNewSessionListEnabled } from '@/app-shell/flags/session-flags';
import {
  CHRONICLE_ORIGIN,
  chronicleBackgroundSessionAtom,
  chronicleDeviceRecordingInfoAtom,
  chronicleInterruptedSessionAtom,
  chronicleIsInputAtom,
  chronicleLiveOfflineAtom,
  chronicleReconnectedPendingEndAtom,
  chronicleRecordingEndReasonAtom,
  chronicleSessionIdAtom,
  chronicleSupportsChunkLiveAtom,
  type ActiveRecordingTransport,
  type ChronicleRecordingEndReason,
} from '@/atoms/chronicle';
import { isFinishedRecordingSession } from '@/hooks/desktop/isFinishedRecordingSession';
import { invalidateSessionsQuery } from '@/hooks/query/useQueryCombinedSessions';
import {
  beginOwnedSessionCreateAttempt,
  cancelSessionCreateAttempt,
  completeSessionCreateAttempt,
  failSessionCreateAttempt,
} from '@/lib/telemetry/sessionCreateTracking';
import { postCreateSessionV2 } from '@/pages/api/scribeAPIV2';
import { store } from '@/store';
import { autoSessionBridge } from '@/tauri/chronicle/auto-session';
import { logHardwareDatadogEvent } from '@/utils/analytics/log-hardware-datadog-event';
import { SESSION_SOURCE_TYPE } from '@/utils/analytics/session-source-type';
import {
  trackHardwareDeviceRecordingStopped,
  trackHardwareRecordingStartBlocked,
} from '@/utils/analytics/track-hardware-events';
import { getSessionCreationPlatform } from '@/utils/sessionCreationPlatform';
import { toast } from '@heidi/design-system/toast';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import {
  BackupUploadingStatus,
  buildSessionPath,
  DEFAULT_TEMPLATE_AUTO_MODE,
  GET_SESSION_BY_ID,
  StreamingStatus,
  type QuerySessionResponse,
  type SessionResponse,
} from '@heidi/session/contracts';
import { activeSessionIdAtom, mapNewBrain } from '@heidi/session/data';
import { getCurrentUser } from '@heidi/workspace/data';
import { AudioLines, Info } from 'lucide-react';
import { useChroniclePauseRecording } from './useChroniclePauseRecording';
import { useChronicleResumeRecording } from './useChronicleResumeRecording';
import { useChronicleStartRecording } from './useChronicleStartRecording';
import {
  useChronicleStopRecording,
  type ChronicleStopOutcome,
} from './useChronicleStopRecording';

/**
 * Resolve `is_auto_mode` the same way New Session does: AUTO sentinel → true;
 * a stored specific default (or none) → false so the backend applies that
 * template rather than forcing Auto.
 */
export function resolveCreateSessionIsAutoMode(
  defaultTemplateId: string | null | undefined
): boolean {
  return defaultTemplateId === DEFAULT_TEMPLATE_AUTO_MODE;
}

/**
 * Seed GET_SESSION_BY_ID from a create response — field parity with
 * useMutateCreateNewSession's newSessionData (incl. mapNewBrain), plus voice
 * hardening for Remote when the create response omits voice_style.
 */
export function seedSessionCacheFromCreateResponse(
  queryClient: QueryClient,
  data: SessionResponse
): void {
  const sessionId = data.session_id;
  const consultNote = data.consult_note;
  const userDefaultVoiceStyle = getCurrentUser()?.session_settings?.default_voice_style;
  const newSessionData: QuerySessionResponse = {
    ...data,
    streamingStatus: StreamingStatus.NOT_STARTED,
    chunkUploadProgress: undefined,
    backupUploadingStatus: BackupUploadingStatus.NOT_STARTED,
    userDismissedNoSoundDetectedModal: false,
    shouldShowNoteSyncChanges: false,
    shouldShowDocumentSyncChanges: false,
    isSocketError: false,
    chunksUploading: [],
    chunksUploadingFinished: false,
    abortController: new AbortController(),
    isDocumentFromNote: false,
    isAutoGeneratedNoteWarningDismissed: false,
    consult_note: {
      ...consultNote,
      brain: mapNewBrain(consultNote?.brain),
      voice_style: consultNote?.voice_style ?? userDefaultVoiceStyle ?? null,
    },
  };
  queryClient.setQueryData([GET_SESSION_BY_ID, { sessionId }], newSessionData);
}

// ---------------------------------------------------------------------------
// Guard: is any recording already in progress?
// ---------------------------------------------------------------------------

export function isRecordingTimerRunning(): boolean {
  return store.getState().timerModel.isRunning;
}

export function getRecordingTimerSeconds(): number {
  return Number(store.getState().timerModel.seconds);
}

/**
 * Checks whether a recording is already in progress on the given session,
 * regardless of source (Chronicle, mic, or speaker).
 *
 * Uses three independent layers because each source of truth has caveats:
 *
 * - **Layer 1 – Redux timer** (`timerModel.isRunning`): most reliable signal.
 *   Always set synchronously on any recording start. Not subject to cache
 *   invalidation or background refetches.
 *
 * - **Layer 2 – `chronicleSessionId`**: set when a Chronicle recording is
 *   started, cleared on stop. Covers the case where a Chronicle session is
 *   active but the timer has already been paused/stopped.
 *
 * - **Layer 3 – query cache `recording_status`**: the canonical backend field.
 *   However, it is set as an optimistic update for `RECORDING` and is never
 *   persisted to the backend. A background refetch (e.g. window focus) can
 *   silently overwrite it back to `IDLE`, so it cannot be trusted alone.
 *
 * @returns An object with `inProgress: true` and a human-readable `reason`
 *          if a recording is active, or `{ inProgress: false }` otherwise.
 */
export function isRecordingInProgress(opts: {
  sessionId: string;
  chronicleSessionId: string | null;
  queryClient: QueryClient;
}): { inProgress: true; reason: string } | { inProgress: false } {
  const { sessionId, chronicleSessionId, queryClient } = opts;

  // Layer 1 – Redux timer
  const timerIsRunning = isRecordingTimerRunning();
  if (timerIsRunning) {
    return {
      inProgress: true,
      reason: `Timer is running (sessionId=${sessionId})`,
    };
  }

  // Layer 2 – Active Chronicle session
  if (chronicleSessionId) {
    return {
      inProgress: true,
      reason: `Chronicle session active (chronicleSessionId=${chronicleSessionId})`,
    };
  }

  // Layer 3 – Query cache recording_status
  const sessionData = queryClient.getQueryData<SessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId },
  ]);
  if (
    sessionData?.recording_status &&
    sessionData.recording_status !== RecordingStatus.IDLE
  ) {
    return {
      inProgress: true,
      reason: `Cache recording_status=${sessionData.recording_status}`,
    };
  }

  return { inProgress: false };
}

/**
 * Inverse of {@link isRecordingInProgress} for desktop-mic liveness
 * polling. Only true when native mic/speaker capture should be producing PCM
 * right now — not Chronicle, not PAUSED, not start-in-flight.
 */
export function shouldProbeDesktopMicCapture(opts: {
  sessionId: string;
  activeTransport: ActiveRecordingTransport | null;
  startMutationInFlight: boolean;
  queryClient: QueryClient;
}): boolean {
  if (opts.activeTransport !== 'desktop-mic') return false;
  if (opts.startMutationInFlight) return false;

  const sessionData = opts.queryClient.getQueryData<SessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId: opts.sessionId },
  ]);
  const recordingStatus = sessionData?.recording_status;
  if (recordingStatus === RecordingStatus.PAUSED) return false;

  const timerIsRunning = isRecordingTimerRunning();
  return timerIsRunning || recordingStatus === RecordingStatus.RECORDING;
}

/**
 * Single post-stop navigation owner (APP-7870, Pitfall 8 / iOS PR 2507).
 * Navigate only when this handler's own stop performed the flow (`'stopped'`);
 * a `'duplicate'` outcome is the device echo of an app-initiated stop, which
 * owns the post-stop UX. Also skipped while any session detail is open (iOS
 * `navigationPathContainsSessionDetail` equivalent) and for BLE-interrupted
 * stops, which await bulk-sync recovery rather than review.
 */
export function shouldNavigateAfterDeviceStop(args: {
  stopOutcome: ChronicleStopOutcome;
  isInterrupted: boolean;
  stoppedSessionId: string | null;
  currentSessionId: string | null;
  isRemoteNewSessionLogicEnabled: boolean;
}): boolean {
  return (
    args.isRemoteNewSessionLogicEnabled &&
    args.stopOutcome === 'stopped' &&
    !args.isInterrupted &&
    args.stoppedSessionId !== null &&
    args.currentSessionId === null
  );
}

/**
 * Device-initiated STOP handoff classification (HAR-1816 Soft-PASS A).
 *
 * On firmware 4.x (`supportsChunkLive === false`) this is the historical
 * classifier: interrupt only for `ble_disconnect` / `poweroff_batlow`. Clean
 * reasons already yielded `isInterrupted: false`, so chunk-live clean stops
 * skipped bulk-sync recovery only by coincidence of `recordingEndReason`.
 *
 * Soft-PASS A makes the chunk-live clean path explicit: when chunk-live is
 * supported and the end reason is clean (`normal_stop` / `max_duration`),
 * force the non-interrupt handoff (no `setInterruptedSession`, stop with
 * `isInterrupted: false`). Interrupt paths on chunk-live stay unchanged.
 * The `!supportsChunkLive` branch must stay byte-identical — pin it in tests.
 */
export function classifyDeviceInitiatedStopHandoff(args: {
  recordingEndReason: ChronicleRecordingEndReason | null;
  supportsChunkLive: boolean;
}): {
  isInterrupted: boolean;
  isPoweroffBatlow: boolean;
  /** True only for Soft-PASS A's deliberate chunk-live clean-stop branch. */
  isChunkLiveCleanStop: boolean;
} {
  const isPoweroffBatlow = args.recordingEndReason === 'poweroff_batlow';
  const isCleanStop =
    args.recordingEndReason === 'normal_stop' ||
    args.recordingEndReason === 'max_duration';

  if (args.supportsChunkLive && isCleanStop) {
    return {
      isInterrupted: false,
      isPoweroffBatlow: false,
      isChunkLiveCleanStop: true,
    };
  }

  // 4.x (every path) + chunk-live interrupt — unchanged from tip before Soft-PASS A.
  return {
    isInterrupted: args.recordingEndReason === 'ble_disconnect' || isPoweroffBatlow,
    isPoweroffBatlow,
    isChunkLiveCleanStop: false,
  };
}

/**
 * Orchestration hook — listens to Chronicle device recording state and handles
 * device-initiated recording starts, stops, pauses, and resumes.
 *
 * **Start detection**: `chronicleDeviceRecordingInfoAtom` transitions from
 * `null` → non-null (device button pressed). Routes to
 * `useChronicleStartRecording` with origin 'remote_device'.
 *
 * **Stop detection**: `chronicleDeviceRecordingInfoAtom` transitions from
 * non-null → `null` while `chronicleSessionIdAtom` is set. Routes to
 * `useChronicleStopRecording` with origin 'remote_device'.
 *
 * **Pause detection**: `chronicleDeviceRecordingInfoAtom.isPaused` transitions
 * from `false` → `true` while recording. Routes to
 * `useChroniclePauseRecording` with origin 'remote_device'.
 *
 * **Resume detection**: `chronicleDeviceRecordingInfoAtom.isPaused` transitions
 * from `true` → `false` while recording. Routes to
 * `useChronicleResumeRecording` with origin 'remote_device'.
 *
 * All paths follow the same unified hooks as desktop-initiated actions,
 * ensuring consistent state management regardless of origin.
 */
// APP-7607: module-level (shared across every listener invocation / RUM view
// churn within the single webview) dedup of device-initiated online-session
// starts. The per-instance `isHandlingRef`/`isStarting` guards don't stop a
// single device recording edge that fans out to multiple start-detectors;
// this Set does. Keyed on the device recording's stable identity — its
// `fileName`, falling back to `startedAt` (present on the first emit, unlike
// the async `fileName`). Cleared on the genuine device-stop (null) transition.
const startedDeviceRecordingKeys = new Set<string>();

/**
 * Test-only: clear the module-level device-start dedup set so cases don't leak
 * keys into one another (the set is module-scoped by design — see above).
 */
export function __resetDeviceStartDedupForTests(): void {
  startedDeviceRecordingKeys.clear();
}

export function useChronicleSessionControl() {
  const intl = useIntl();
  const recordingInfo = useAtomValue(chronicleDeviceRecordingInfoAtom);
  const chronicleSessionId = useAtomValue(chronicleSessionIdAtom);
  const recordingEndReason = useAtomValue(chronicleRecordingEndReasonAtom);
  const interruptedSession = useAtomValue(chronicleInterruptedSessionAtom);
  const liveOffline = useAtomValue(chronicleLiveOfflineAtom);
  const reconnectedPendingEnd = useAtomValue(chronicleReconnectedPendingEndAtom);
  const isChronicleInput = useAtomValue(chronicleIsInputAtom);
  const supportsChunkLive = useAtomValue(chronicleSupportsChunkLiveAtom);
  const setInterruptedSession = useSetAtom(chronicleInterruptedSessionAtom);
  const setBackgroundSession = useSetAtom(chronicleBackgroundSessionAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const queryClient = useQueryClient();
  const { startChronicleRecording, isStarting } = useChronicleStartRecording();
  const { stopChronicleRecording } = useChronicleStopRecording();
  const { pauseChronicleRecording } = useChroniclePauseRecording();
  const { resumeChronicleRecording } = useChronicleResumeRecording();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const isRemoteNewSessionLogicEnabled = useIsRemoteNewSessionLogicEnabled();
  const isRemoteSyncAsyncEnabled = useIsReleaseRemoteSyncAsyncWebEnabled();
  const { interruptedSet } = useQueryInterruptedSessions();

  // Shared by both device-start refusals — the Remote keeps recording to the
  // device either way, and bulk sync files it afterwards.
  const showRecordingOfflineToast = useCallback(() => {
    toast({
      title: intl.formatMessage({
        defaultMessage: 'Heidi Remote is recording offline',
        id: 'ySZdgSqx12',
        description:
          'Toast title when Chronicle device starts but a recording is already active',
      }),
      description: intl.formatMessage({
        defaultMessage:
          'A session recording is already in progress. The Heidi Remote recording will be saved to the device and can be synced later.',
        id: '75GzDb+ENl',
        description: 'Toast description explaining Chronicle offline recording behaviour',
      }),
      tone: 'subtle',
      icon: createElement(Info),
      variant: 'outline',
      duration: 8000,
      position: 'bottom-right',
    });
  }, [intl]);

  const prevRecordingRef = useRef(recordingInfo);
  const isHandlingRef = useRef(false);

  // Mirror Provider-scoped atom values into refs so the async start handler
  // always reads the latest value at the moment the device transition fires,
  // without re-running the effect on every input/interrupted-session change.
  const interruptedSessionRef = useRef(interruptedSession);
  interruptedSessionRef.current = interruptedSession;
  const liveOfflineRef = useRef(liveOffline);
  liveOfflineRef.current = liveOffline;
  const reconnectedPendingEndRef = useRef(reconnectedPendingEnd);
  reconnectedPendingEndRef.current = reconnectedPendingEnd;
  const isRemoteSyncAsyncEnabledRef = useRef(isRemoteSyncAsyncEnabled);
  isRemoteSyncAsyncEnabledRef.current = isRemoteSyncAsyncEnabled;
  const isChronicleInputRef = useRef(isChronicleInput);
  isChronicleInputRef.current = isChronicleInput;
  const isNewSessionListEnabledRef = useRef(isNewSessionListEnabled);
  isNewSessionListEnabledRef.current = isNewSessionListEnabled;
  const isRemoteNewSessionLogicEnabledRef = useRef(isRemoteNewSessionLogicEnabled);
  isRemoteNewSessionLogicEnabledRef.current = isRemoteNewSessionLogicEnabled;
  const interruptedSetRef = useRef(interruptedSet);
  interruptedSetRef.current = interruptedSet;
  const supportsChunkLiveRef = useRef(supportsChunkLive);
  supportsChunkLiveRef.current = supportsChunkLive;

  useEffect(() => {
    const prev = prevRecordingRef.current;
    prevRecordingRef.current = recordingInfo;

    // APP-7607: clear the device-start dedup keys on the genuine stop
    // (non-null → null) transition, independent of chronicleSessionId, so the
    // next recording starts fresh even if a stop raced ahead of the
    // session-id claim.
    if (!recordingInfo && prev !== null) {
      startedDeviceRecordingKeys.clear();
    }

    if (isHandlingRef.current || isStarting) return;

    // --- Detect device-initiated START: null → non-null ---
    if (recordingInfo && prev === null) {
      // If a Chronicle session is already active, the device is acknowledging
      // an app-initiated start — not a new device-initiated recording.
      // Skip to avoid false "recording offline" toasts.
      if (chronicleSessionId) {
        logger.debug(
          '[useChronicleSessionControl] Device recording state confirmed for existing Chronicle session — skipping start handler',
          { chronicleSessionId }
        );
        return;
      }

      isHandlingRef.current = true;

      const { startedAt } = recordingInfo;

      const handleDeviceStarted = async () => {
        try {
          // Window focus is now handled by Rust (auto_session module) immediately
          // when the device state transitions to recording, independent of
          // WebView throttling.

          // BLE reconnection guard: if an interrupted session exists, use
          // fileName matching (FW 1.7+) to distinguish a genuine reconnect
          // to the same recording from a new device-initiated start.
          //
          // APP&FNAME is sent before APP&STE in send_post_connect_init so
          // the fileName is typically available by this point. If both
          // fileNames are available and differ, this is a new recording —
          // clear the stale interrupted session and proceed normally.
          // If fileName is unavailable (old firmware), fall back to assuming
          // reconnect for safety.
          // APP-7867: while live-offline is held (APP-7866), the interrupted
          // session is stale by construction — skip this reconnect/finalize
          // decision entirely and let the recovered listener (PR10) resolve
          // it once the hold clears.
          const interruptedSessionSnapshot = liveOfflineRef.current
            ? null
            : interruptedSessionRef.current;
          if (interruptedSessionSnapshot) {
            const reconnectFileName = recordingInfo.fileName;
            const interruptedFileName = interruptedSessionSnapshot.fileName;

            if (
              reconnectFileName &&
              interruptedFileName &&
              reconnectFileName !== interruptedFileName
            ) {
              logger.info(
                '[useChronicleSessionControl] New recording detected (fileName mismatch) — ' +
                  'clearing stale interrupted session and continuing to the start ' +
                  'routing decision, which may still refuse.',
                {
                  interruptedFileName,
                  reconnectFileName,
                  interruptedSessionId: interruptedSessionSnapshot.sessionId,
                }
              );
              setInterruptedSession(null);
              // Fall through to normal start flow
            } else {
              logger.info(
                '[useChronicleSessionControl] BLE reconnection detected for interrupted session — ' +
                  'skipping recording restart. Session will sync via Bulk Sync after device stops.',
                {
                  sessionId: interruptedSessionSnapshot.sessionId,
                  interruptedFileName,
                  reconnectFileName,
                  matched: reconnectFileName === interruptedFileName,
                }
              );
              setInterruptedSession(null);
              return;
            }
          }

          // APP-7607: claim this physical recording exactly once. Placed after
          // the interrupted-session reconnect block (which returns early, so a
          // reconnect-skip doesn't consume the key) and before BOTH the
          // create-new-session and attach branches below.
          const dedupKey = recordingInfo.fileName ?? startedAt;
          if (startedDeviceRecordingKeys.has(dedupKey)) {
            logger.info(
              '[useChronicleSessionControl] Duplicate device-initiated start for the same recording — skipping (APP-7607)',
              { dedupKey }
            );
            return;
          }
          startedDeviceRecordingKeys.add(dedupKey);

          const currentSessionId = getCurrentSessionId();

          // A session that has already been recorded and stopped is closed to
          // further recording (desktop equivalent of iOS `.closed`). Read the
          // route session from cache to detect that case.
          //
          // Read synchronously from the cache: on a session page the
          // `GET_SESSION_BY_ID` query is an *active* observer, so TanStack keeps
          // it warm (active queries are not garbage-collected). The only cold
          // read is the sub-second between navigating to a session and the query
          // settling — not realistically reachable with a physical button press.
          // If it ever were, `routeSession` is undefined → `routeSessionFinished`
          // is false → the recording attaches to the route session (the pre-PR
          // behaviour), which is benign, not a hard failure.
          const routeSession = currentSessionId
            ? queryClient.getQueryData<SessionResponse>([
                GET_SESSION_BY_ID,
                { sessionId: currentSessionId },
              ])
            : undefined;
          // Gated behind release-desktop-remote-new-session-ux-logic (APP-6513). When
          // the flag is off this stays false, so a finished route session falls
          // through to the attach/resume path below — the legacy behaviour.
          const routeSessionFinished =
            isRemoteNewSessionLogicEnabledRef.current &&
            isFinishedRecordingSession(routeSession);

          // A session awaiting bulk-sync recovery already owns a recording whose
          // audio has not reached the server, so it still reads duration 0 / no
          // audio and `isFinishedRecordingSession` cannot see it. Ungated:
          // filing a second consult into it is a patient-safety failure, not
          // the flag's UX preference. `chronicleSessionId` is necessarily null
          // here — the active-session case returned above.
          const routeSessionInterrupted =
            !!currentSessionId && interruptedSetRef.current.has(currentSessionId);

          logger.info(
            '[useChronicleSessionControl] Device-initiated start routing decision',
            {
              currentSessionId,
              isChronicleInput: isChronicleInputRef.current,
              recordingStatus: routeSession?.recording_status,
              duration: routeSession?.duration,
              audioCount: routeSession?.audio?.length ?? 0,
              routeSessionFinished,
              routeSessionInterrupted,
            }
          );

          // Create a NEW session (instead of recording into the route session)
          // when there is nothing safe to attach to:
          //  - no session route is open, or
          //  - the clinician picked built-in mic / system audio, not the
          //    Remote, so leave that session alone, or
          //  - the route session is finished — the Remote always starts a fresh
          //    session rather than resuming a stopped one (iOS parity).
          if (!currentSessionId || !isChronicleInputRef.current || routeSessionFinished) {
            const timerIsRunning = store.getState().timerModel.isRunning;
            if (timerIsRunning || chronicleSessionId) {
              logger.warn(
                '[useChronicleSessionControl] Recording already active, skipping background session creation',
                { timerIsRunning, chronicleSessionId }
              );
              return;
            }

            // A Remote press owns an instanced attempt, closed here, so it never
            // touches the shared UI entry. It does not navigate, so success is the
            // session id in hand and its cache seeded, not a page render.
            const createAttempt = beginOwnedSessionCreateAttempt({
              entryPoint: 'heidi_remote',
              create_mode: 'online',
            });

            // Get session from Rust auto-session (already created or in progress).
            // Falls back to frontend creation if Rust couldn't create one.
            //
            // Rust suppresses auto-session creation whenever a session route is
            // open (see auto_session.rs), so getOrWaitSession() would block for
            // its full timeout in that case. Only wait on Rust when no route is
            // open; otherwise fall straight through to frontend creation.
            let newSessionId: string | null;
            try {
              newSessionId = currentSessionId
                ? null
                : await autoSessionBridge.getOrWaitSession();
            } catch (error) {
              if (createAttempt)
                failSessionCreateAttempt(error, { handle: createAttempt });
              throw error;
            }

            if (newSessionId) {
              logger.info(
                '[useChronicleSessionControl] Using Rust-created auto-session',
                { sessionId: newSessionId }
              );
              if (createAttempt) {
                completeSessionCreateAttempt(
                  { handle: createAttempt },
                  { create_source: 'rust_auto_session' }
                );
              }
            } else {
              const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              const sessionSettings = getCurrentUser()?.session_settings;
              const userDefaultTemplateId = sessionSettings?.default_template_id;
              const is_auto_mode = resolveCreateSessionIsAutoMode(userDefaultTemplateId);
              logger.info(
                '[useChronicleSessionControl] Rust auto-session unavailable — creating session from frontend',
                {
                  isAutoMode: is_auto_mode,
                  hasUserSettings: !!sessionSettings,
                  hasDefaultTemplateId: !!userDefaultTemplateId,
                }
              );
              let response: Awaited<ReturnType<typeof postCreateSessionV2>>;
              try {
                response = await postCreateSessionV2({
                  timezone,
                  is_auto_mode,
                  creation_platform: getSessionCreationPlatform({ isHardware: true }),
                  signal: AbortSignal.timeout(60_000),
                });
              } catch (error) {
                if (createAttempt) {
                  failSessionCreateAttempt(error, { handle: createAttempt });
                }
                throw error;
              }
              // The HTTP layer resolves empty when a 401 or a suspended account
              // takes the user out of the app, so no session was created.
              if (!response) {
                if (createAttempt) {
                  cancelSessionCreateAttempt('interrupted', { handle: createAttempt });
                }
                throw new Error('create_session_empty_response');
              }
              const { data } = response;
              seedSessionCacheFromCreateResponse(queryClient, data);
              newSessionId = data.session_id;
              if (createAttempt) {
                completeSessionCreateAttempt(
                  { handle: createAttempt },
                  { create_source: 'frontend' }
                );
              }
            }

            // Finished-session restarts start the timer from 0 — the device just
            // started, so there's no prior elapsed to honour, and it avoids the
            // timer jumping ahead by the create+start+navigate latency. Other
            // background sessions keep the device `startedAt` offset (the legacy
            // behaviour). Tied to routeSessionFinished, so it is flag-gated too —
            // flag-off never reaches the from-0 path.
            await startChronicleRecording({
              origin: CHRONICLE_ORIGIN.DEVICE,
              targetSessionId: newSessionId,
              startedAt: routeSessionFinished ? undefined : startedAt,
            });

            setBackgroundSession({
              sessionId: newSessionId,
              createdAt: Date.now(),
            });
            setActiveSessionId(newSessionId);

            // Confirm only AFTER the recording has actually started. Firing this
            // before the create/start awaits would risk a green "started" toast
            // contradicting the red "Failed to start recording" toast from the
            // catch block if creation/start throws.
            toast({
              title: intl.formatMessage({
                defaultMessage: 'New session started via Heidi Remote',
                id: 'RqfxZsWdBv',
                description:
                  'Toast shown when a Chronicle device initiates a background recording session',
              }),
              variant: 'outline',
              tone: 'success',
              icon: createElement(AudioLines),
            });

            // Refresh the session list after recording is live — the new session
            // page fetches itself by id, so the list refetch is off the critical
            // path and would only add render churn during the navigation.
            void invalidateSessionsQuery();

            // If the clinician was viewing a finished session, move them to the
            // freshly created one so the active/highlighted session matches what
            // is recording — otherwise the old (finished) session stays selected
            // while the new one records in the background. Uses the flag-correct
            // route to avoid the `/scribe/session` → `/scribe/sessions` redirect
            // that caused a full page refresh. (The not-on-a-session-page case
            // is handled at stop instead, so it records quietly in the background.)
            if (routeSessionFinished) {
              await Router.push(
                buildSessionPath(newSessionId, isNewSessionListEnabledRef.current)
              );
            }

            logger.debug(
              '[useChronicleSessionControl] Background session created and recording started',
              { sessionId: newSessionId }
            );
            return;
          }

          // Guard: ignore device-initiated start if ANY recording is active.
          const recordingCheck = isRecordingInProgress({
            sessionId: currentSessionId,
            chronicleSessionId,
            queryClient,
          });

          if (recordingCheck.inProgress) {
            logger.info(
              '[useChronicleSessionControl] Recording already in progress, ' +
                'ignoring device-initiated start. Chronicle records offline.',
              {
                sessionId: currentSessionId,
                reason: recordingCheck.reason,
              }
            );
            // Normalize free-text reason into a low-cardinality key (raw string has session IDs).
            const blockedReason = recordingCheck.reason.startsWith('Timer')
              ? 'timer_running'
              : recordingCheck.reason.startsWith('Chronicle session')
                ? 'chronicle_session_active'
                : 'cache_recording_status';
            trackHardwareRecordingStartBlocked({
              sessionIdLocal: currentSessionId,
              reason: blockedReason,
            });

            showRecordingOfflineToast();
            return;
          }

          // Refuse to bind a live session to a route session awaiting recovery
          // — deliberately WITHOUT choosing a different session. The frontend
          // cannot tell a reconnect continuing this session's own recording
          // from a new consult (the interruption toast clears
          // `chronicleInterruptedSessionAtom`, and the device file may not be
          // known yet), and it does not have to: the Remote keeps recording
          // and bulk sync files it afterwards — into this session if it was a
          // continuation, as its own `created_offline` session if it was not.
          if (routeSessionInterrupted) {
            logger.info(
              '[useChronicleSessionControl] Route session is awaiting bulk-sync ' +
                'recovery — recording offline instead of attaching',
              { sessionId: currentSessionId }
            );
            trackHardwareRecordingStartBlocked({
              sessionIdLocal: currentSessionId,
              reason: 'route_session_interrupted',
            });

            showRecordingOfflineToast();
            return;
          }

          logger.debug(
            '[useChronicleSessionControl] Device-initiated recording detected, starting unified flow',
            { sessionId: currentSessionId, startedAt }
          );

          await startChronicleRecording({
            origin: CHRONICLE_ORIGIN.DEVICE,
            targetSessionId: currentSessionId,
            startedAt,
          });
        } catch (error) {
          logger.error(
            '[useChronicleSessionControl] Failed to handle device-initiated start:',
            error
          );

          toast({
            title: intl.formatMessage({
              defaultMessage: 'Failed to start recording',
              id: 'SUObbkKw4M',
              description:
                'Toast title when background session creation fails on device-initiated recording',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Heidi Remote is still recording on the device. You can sync the recording later.',
              id: 'pPr8NWS7aa',
              description: 'Toast description when background session creation fails',
            }),
            tone: 'destructive',
            variant: 'outline',
            duration: 8000,
            position: 'bottom-right',
          });
        } finally {
          isHandlingRef.current = false;
        }
      };

      void handleDeviceStarted();
      return;
    }

    // --- Detect device-initiated STOP: non-null → null, with active session ---
    // during the recovery window (reconnected-pending-end, or a
    // live-offline hold) most recordingInfo → null echoes are spurious
    // transients, not a device stop — the device is KNOWN to be recording, so
    // ignore them (only a manual app stop ends it). The exception is a GENUINE
    // device stop the user made on the device: `process-recording-drop` lets it
    // through as a clean `normal_stop` / `max_duration` once the device's STE
    // round-trip resolves to no-longer-recording (and only such a stop nulls
    // recordingInfo in the window — spurious/interrupted echoes stay held
    // upstream). Exempt that stop via the shared `recordingEndReason`
    // distinguisher so the desktop session ends and hands off to bulk-sync
    // on 4.x (on-device file is the only complete copy). Soft-PASS A
    // (HAR-1816): on chunk-live, a clean stop is deliberate — audio is already
    // on the server, so classifyDeviceInitiatedStopHandoff skips the interrupt /
    // bulk-sync recovery handoff while still calling stopChronicleRecording
    // with isInterrupted: false. Interrupt reasons keep today's recovery path.
    //
    // Flag × endReason matrix (release-apps-remote-sync-async-web):
    // - Flag ON + recovery window + clean stop (`normal_stop`|`max_duration`):
    //   exempt → finalize (isInterrupted=false).
    // - Flag ON + recovery window + other reason: suppress (do not stop).
    // - Flag OFF: guard inert — every recordingInfo→null stop fires (legacy).
    // `processRecordingDrop` tags `max_duration` from `lastStopReason` on any
    // connected/syncing finalize regardless of this flag (additive reason;
    // sync-async does not own firmware cap attribution). The max-duration toast
    // is ungated (Tom option 1 / Tim preference #1) — device already stopped;
    // toast is explanatory-only and is not owned by this sync-async flag.
    const isCleanDeviceStop =
      recordingEndReason === 'normal_stop' || recordingEndReason === 'max_duration';
    if (
      !recordingInfo &&
      prev !== null &&
      chronicleSessionId &&
      !(
        isRemoteSyncAsyncEnabledRef.current &&
        (reconnectedPendingEndRef.current === chronicleSessionId ||
          liveOfflineRef.current !== null) &&
        !isCleanDeviceStop
      )
    ) {
      isHandlingRef.current = true;
      const stoppedSessionId = chronicleSessionId;

      // Soft-PASS A (HAR-1816): read chronicleSupportsChunkLiveAtom so chunk-live
      // clean stops are an explicit non-interrupt path, not an accident of
      // recordingEndReason. 4.x (!chunk-live) keeps the historical classifier.
      const { isInterrupted, isPoweroffBatlow } = classifyDeviceInitiatedStopHandoff({
        recordingEndReason,
        supportsChunkLive: supportsChunkLiveRef.current,
      });

      if (isInterrupted) {
        if (!isPoweroffBatlow) {
          setInterruptedSession({
            sessionId: chronicleSessionId,
            timestamp: Date.now(),
            fileName: prev?.fileName,
          });
        }
        logHardwareDatadogEvent(
          'hardware_recording_interrupted',
          {
            session_source_type: SESSION_SOURCE_TYPE.ChronicleLiveRecording,
            session_id: chronicleSessionId,
            session_id_local: chronicleSessionId,
            reason: 'ble_disconnect',
          },
          'warn'
        );
      }

      // Rust can hold a `live_recording` row at `session_created` after ANY end
      // that reaches here — including a normal stop that followed a live-offline
      // hold — and the row state, not our interruption classification, is what
      // the interrupted set reports. The query is `staleTime: Infinity`, so
      // missing this invalidation leaves the next device start reading a stale
      // empty set.
      void invalidateInterruptedSessions();

      const handleDeviceStopped = async () => {
        try {
          logger.debug(
            '[useChronicleSessionControl] Device-initiated stop detected, triggering unified stop flow',
            { sessionId: chronicleSessionId, isInterrupted }
          );

          trackHardwareDeviceRecordingStopped({
            sessionIdLocal: chronicleSessionId,
            wasBleDisconnected: isInterrupted,
          });

          setBackgroundSession(null);

          const stopOutcome = await stopChronicleRecording({
            origin: CHRONICLE_ORIGIN.DEVICE,
            isInterrupted,
          });

          // Open the recorded session if the clinician wasn't already viewing a
          // session (e.g. it recorded in the background). If they're on a
          // session detail page, leave them where they are. Skipped for
          // BLE-interrupted stops — those await bulk-sync recovery, not review —
          // and when this transition was only the device echo of an
          // app-initiated stop (see shouldNavigateAfterDeviceStop).
          // Uses the flag-correct route so we don't bounce through the
          // `/scribe/session` → `/scribe/sessions` redirect.
          if (
            shouldNavigateAfterDeviceStop({
              stopOutcome,
              isInterrupted,
              stoppedSessionId,
              currentSessionId: getCurrentSessionId() ?? null,
              isRemoteNewSessionLogicEnabled: isRemoteNewSessionLogicEnabledRef.current,
            })
          ) {
            await Router.push(
              buildSessionPath(stoppedSessionId, isNewSessionListEnabledRef.current)
            );
          }
        } catch (error) {
          logger.error(
            '[useChronicleSessionControl] Failed to handle device-initiated stop:',
            error
          );
        } finally {
          isHandlingRef.current = false;
        }
      };

      void handleDeviceStopped();
      return;
    }

    // --- Detect device-initiated PAUSE: isPaused false → true ---
    if (
      recordingInfo &&
      prev &&
      !prev.isPaused &&
      recordingInfo.isPaused &&
      chronicleSessionId
    ) {
      isHandlingRef.current = true;

      const handleDevicePaused = async () => {
        try {
          logger.debug('[useChronicleSessionControl] Device-initiated pause detected', {
            sessionId: chronicleSessionId,
          });

          await pauseChronicleRecording({ origin: CHRONICLE_ORIGIN.DEVICE });
        } catch (error) {
          logger.error(
            '[useChronicleSessionControl] Failed to handle device-initiated pause:',
            error
          );
        } finally {
          isHandlingRef.current = false;
        }
      };

      void handleDevicePaused();
      return;
    }

    // --- Detect device-initiated RESUME: isPaused true → false ---
    if (
      recordingInfo &&
      prev &&
      prev.isPaused &&
      !recordingInfo.isPaused &&
      chronicleSessionId
    ) {
      isHandlingRef.current = true;

      const handleDeviceResumed = async () => {
        try {
          logger.debug('[useChronicleSessionControl] Device-initiated resume detected', {
            sessionId: chronicleSessionId,
          });

          await resumeChronicleRecording({ origin: CHRONICLE_ORIGIN.DEVICE });
        } catch (error) {
          logger.error(
            '[useChronicleSessionControl] Failed to handle device-initiated resume:',
            error
          );
        } finally {
          isHandlingRef.current = false;
        }
      };

      void handleDeviceResumed();
    }
  }, [
    recordingInfo,
    chronicleSessionId,
    recordingEndReason,
    queryClient,
    intl,
    setInterruptedSession,
    setBackgroundSession,
    setActiveSessionId,
    showRecordingOfflineToast,
    startChronicleRecording,
    stopChronicleRecording,
    pauseChronicleRecording,
    resumeChronicleRecording,
    isStarting,
  ]);
}
